import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

// helper methods
import { Button, LinkIcon } from 'reaxl';
import { sendClick, valueEventCode } from 'reaxl-analytics';
import { ndsLendingOptionsCTAClick } from 'reaxl-analytics-handlers';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import WestlakeButton from './components/WestlakeButton';
import getWestlakeApiObject from './utilities';

function Westlake({
    className,
    linkClassName,
    listing = {},
    onWestlakeButtonClick = () => { },
    westlakePreQualUrl = '',
    page,
    placement,
    personalization,
    uiContext = 'button',
    user,
    onError = () => { },
    ...rest
}) {
    const { isWestlakePrequal = false } = listing;

    const [scriptInjected, setScriptInjected] = useState(false);

    const westlakeIframe = useRef();
    const countRef = useRef(0);
    const intervalRef = useRef(null);

    // Setup iFrame environment on first load / SPA transition
    useEffect(() => {
        if (isWestlakePrequal) {
            // Setup event listeners
            const messageLog = [];
            const westlakeMessageListener = (e) => {
                // Pass on messages to iframe that are sent to global window by Westlake script
                if (e?.origin.includes('autotrader.carzing.com') && !messageLog.includes(e?.timeStamp)) {
                    messageLog.push(e.timeStamp);
                    westlakeIframe.current.contentWindow.postMessage(e.data, '*');
                }

                // Handle events for modal interactions
                if (e?.data?.type === 'WESTLAKE_MODAL_OPENED') {
                    westlakeIframe.current.style.opacity = '1';
                    westlakeIframe.current.style.left = '0';
                }
                if (e?.data?.type === 'WESTLAKE_MODAL_CLOSED') {
                    westlakeIframe.current.style.opacity = '0';
                    westlakeIframe.current.style.left = '-100vw';
                }
            };
            window.addEventListener('message', westlakeMessageListener);

            // Get ref for iFrame
            if (!westlakeIframe.current) {
                let westlakeIframeDOM = document.getElementById('westlakeIntegration');
                if (!westlakeIframeDOM) {
                    westlakeIframeDOM = document.createElement('iframe');
                    westlakeIframeDOM.setAttribute('id', 'westlakeIntegration');
                    westlakeIframeDOM.setAttribute('title', 'Westlake Financing: Get Prequalified');
                    westlakeIframeDOM.setAttribute('src', `/cars-for-sale/assets/atc/westlake.html?url=${westlakePreQualUrl}`);
                    westlakeIframeDOM.style.width = '100vw';
                    westlakeIframeDOM.style.height = '100vh';
                    westlakeIframeDOM.style.opacity = '0';
                    westlakeIframeDOM.style.position = 'fixed';
                    westlakeIframeDOM.style.top = '0';
                    westlakeIframeDOM.style.left = '-100vw';
                    westlakeIframeDOM.style.zIndex = 2147483646;
                    westlakeIframeDOM.classList.add('border-0');
                    document.body.appendChild(westlakeIframeDOM);
                }
                westlakeIframe.current = westlakeIframeDOM;
            }

            // Wait for carzing script to load
            const monitorScriptLoaded = () => {
                try {
                    if (westlakeIframe?.current?.contentWindow?.carzing) {
                        setScriptInjected(true);
                        return true;
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
                return false;
            };

            intervalRef.current = setInterval(() => {
                countRef.current += 1;

                if (scriptInjected || monitorScriptLoaded() || countRef.current > 15) {
                    clearInterval(intervalRef.current);
                }

                if (countRef.current > 15) {
                    onError();
                }
            }, 200);

            return () => {
                window.removeEventListener('message', westlakeMessageListener);

                if (intervalRef?.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }

        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWestlakePrequal, listing.id]);

    if (!isWestlakePrequal || !scriptInjected) {
        return null;
    }

    const openWestlakePrequalModal = () => {
        try {
            // named for debugger visibility
            const init = getWestlakeApiObject(listing);
            westlakeIframe.current.contentWindow.postMessage({ type: 'WESTLAKE_INIT', payload: init }, '*');
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
        }
    };

    const onWestlakeClick = (event) => {
        event.stopPropagation();
        onWestlakeButtonClick();
        const isGetPreQualified = ['link', 'calculator', 'default-button'].includes(uiContext);

        const data = {
            epn: 'Westlake Financing',
            pixallData: {
                applicationType: 'loan',
                eventSource: 'application',
                feature: 'caca',
                label: `get_loan-${['nds', 'nba', 'price-card'].includes(placement) ? placement.replace(/-/g, '') : 'top'}`,
                vendor: 'westlake',
                cdl: {
                    eventProperties: {
                        event: {
                            valueEventCode: isGetPreQualified ? valueEventCode.CREDIT_APP : valueEventCode.WESTLAKE,
                        },
                    },
                },
            },
            placement,
            uiContext,
        };

        if (['button', 'link', 'calculator', 'default-button'].includes(uiContext)) {
            sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
        } else {
            sendClick(ndsLendingOptionsCTAClick, event, {
                listingId: listing?.id,
                placement,
                uiContext,
                vendor: 'westlake',
                isWestlakeEnabled: true,
            });
        }

        // Trigger Westlake modal
        if (scriptInjected) {
            openWestlakePrequalModal();
        }
    };

    switch (uiContext) {
        case 'link':
        case 'calculator':
            return (
                <LinkIcon
                    data-cmp="westlake-prequal-cta"
                    key="westlakeLink"
                    className={linkClassName}
                    glyph={uiContext === 'calculator' ? 'chevron-right' : ''}
                    onClick={onWestlakeClick}
                    undecorated
                >
                    Get Pre-Qualified
                </LinkIcon>
            );
        case 'default-button':
            return (
                <Button
                    data-cmp="westlake-prequal-cta"
                    key="westlakeButton"
                    className="padding-horizontal-0"
                    block
                    onClick={onWestlakeClick}
                >
                    Get Pre-Qualified
                </Button>
            );
        case 'button':
        default:
            return (
                <div
                    className={clsx('westlake-prequalification-button', className)}
                    data-cmp="westlake-prequal-cta"
                    key="westlakeButton"
                    {...rest}
                >
                    <WestlakeButton
                        onClick={onWestlakeClick}
                        personalization={personalization}
                        listing={listing}
                        user={user}
                    />
                </div>

            );
    }
}

Westlake.propTypes = {
    className: PropTypes.string,
    linkClassName: PropTypes.string,
    /**
     * Standard listing object
     */
    listing: PropTypes.object,
    /**
    * prop needed for retargeting container
    */
    personalization: PropTypes.object,
    /**
     * Callback to handle placeholder click
     */
    onWestlakeButtonClick: PropTypes.func,
    /**
     * Westlake api script url for injection
     */
    westlakePreQualUrl: PropTypes.string,
    /**
     * uiContext that determines the type of CTA
     */
    uiContext: PropTypes.oneOf(['button', 'link', 'calculator', 'tile', 'lenderTile', 'default-button']),
    /**
     * callback to handle error
     */
    onError: PropTypes.func,
};

export default Westlake;
